import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';

// Payroll hooks. The server (apps/api/src/routes/goodday/hr-payroll.ts) decides who may do what;
// the screen asks /access and shows only the buttons that will work.

const BASE = '/api/v2/hr/payroll';

export interface PayrollAccess { canRead: boolean; canManage: boolean; canFinalize: boolean }

export interface PayslipLine { label: string; amount: number; componentCode?: string }

export interface Payslip {
  id: string;
  user_id: string;
  month_year: string;
  earnings: PayslipLine[];
  deductions: PayslipLine[];
  net_salary: number;
  status: 'draft' | 'finalized';
  source: 'structure_engine' | 'monthly_input' | 'excel_upload';
  created_at?: string;
  employee_name?: string;
  paid_days?: number | null;
  total_days?: number | null;
  leave_days?: number | null;
  user: { id: string; display_name?: string; department?: string; position?: string };
}

export interface PayrollRun {
  id: string;
  month_year: string;
  status: 'draft' | 'submitted' | 'rejected' | 'finalized';
  payslip_count: number;
  total_net: number;
  submitted_by: string | null;
  submitted_at?: string;
  rejection_reason?: string;
  finalized_at?: string;
}

export interface PayrollEmployee {
  id: string;
  display_name: string;
  email?: string;
  department?: string;
  position?: string;
  base_salary: number;
  structure_code: string | null;
  structure_name: string | null;
  incentive_percent: number | null;
  bonus: number;
  effective_from?: string | null;
}

export type CalcType = 'fixed_amount' | 'percent_of_base' | 'percent_of_component' | 'slab';

export interface SalaryComponent {
  id: string;
  code: string;
  name: string;
  type: 'earning' | 'deduction';
  calcType: CalcType;
  calcRef: string | null;
  calcOrder: number;
  defaultValue: number | null;
  slabs: { minBase: number; value: number }[];
  taxable: boolean;
  proRata: boolean;
  statutory: boolean;
  regimeAwareTax: boolean;
  active: boolean;
}

export interface SalaryStructure { id: string; code: string; name: string; description: string; componentCodes: string[]; active: boolean }

export interface IncomeTaxConfig {
  regime: 'old' | 'new';
  standardDeduction: number;
  cessPercent: number;
  rebateThreshold: number;
  slabs: { minAnnualIncome: number; rate: number }[];
}

export interface PayrollSettings { applyStatutoryDeductions: boolean; taxRegime: 'old' | 'new'; incomeTaxSlabs: IncomeTaxConfig[] }

export interface PayrollSetup { components: SalaryComponent[]; structures: SalaryStructure[]; settings: PayrollSettings }

export interface GenerateResult {
  message: string;
  generated: number;
  skipped: number;
  blocked: { userId: string; name: string; reason: string }[];
  notes: string[];
}

export interface MonthlyInput { id: string; user_id: string; month_year: string; earnings: PayslipLine[]; deductions: PayslipLine[]; notes: string; user: { display_name?: string; department?: string } }

export type FieldType = 'text' | 'currency' | 'date' | 'number' | 'skip';
export interface FieldMappingEntry { excel_column: string; payslip_field: string; field_type: FieldType }
export interface FieldMappingDoc { version?: number; mappings: FieldMappingEntry[] }
export interface ExcelPreviewRow {
  _row: number;
  employee_name: string;
  email?: string;
  employee_id_code?: string;
  designation?: string;
  bank_name?: string;
  bank_account?: string;
  working_days?: number | null;
  leave_days?: number | null;
  earnings: PayslipLine[];
  deductions: PayslipLine[];
  net_salary: number;
}
export interface ExcelPreviewResult { headers: string[]; preview: ExcelPreviewRow[]; total_rows: number; has_mapping: boolean }

const keys = {
  access: ['payroll', 'access'] as const,
  setup: ['payroll', 'setup'] as const,
  employees: ['payroll', 'employees'] as const,
  payslips: (month: string) => ['payroll', 'payslips', month] as const,
  runs: ['payroll', 'runs'] as const,
  inputs: (month: string) => ['payroll', 'inputs', month] as const,
  mapping: ['payroll', 'field-mapping'] as const,
};

export const usePayrollAccess = () => useQuery({ queryKey: keys.access, queryFn: async () => (await api.get<PayrollAccess>(`${BASE}/access`)).data, staleTime: 60_000 });
export const usePayrollSetup = () => useQuery({ queryKey: keys.setup, queryFn: async () => (await api.get<PayrollSetup>(`${BASE}/setup`)).data });
export const usePayrollEmployees = () => useQuery({ queryKey: keys.employees, queryFn: async () => (await api.get<PayrollEmployee[]>(`${BASE}/employees`)).data });
export const usePayslips = (month: string) => useQuery({ queryKey: keys.payslips(month), queryFn: async () => (await api.get<Payslip[]>(`${BASE}/payslips`, { params: { month_year: month } })).data });
export const usePayrollRuns = () => useQuery({ queryKey: keys.runs, queryFn: async () => (await api.get<PayrollRun[]>(`${BASE}/runs`)).data });
export const useMonthlyInputs = (month: string) => useQuery({ queryKey: keys.inputs(month), queryFn: async () => (await api.get<MonthlyInput[]>(`${BASE}/monthly-input`, { params: { month_year: month } })).data });
export const usePayrollFieldMapping = () => useQuery({ queryKey: keys.mapping, queryFn: async () => (await api.get<FieldMappingDoc>(`${BASE}/field-mapping`)).data });

/** A mutation that refreshes everything payroll shows. */
function useAction<TInput, TResult = unknown>(fn: (input: TInput) => Promise<TResult>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => { qc.invalidateQueries({ queryKey: ['payroll'] }); } });
}

export const useGeneratePayslips = () => useAction(async (month_year: string) => (await api.post<GenerateResult>(`${BASE}/generate-bulk`, { month_year })).data);
export const useFinalizePayslip = () => useAction(async (id: string) => (await api.post(`${BASE}/${id}/finalize`)).data);
export const useEmailPayslip = () => useAction(async (id: string) => (await api.post(`${BASE}/${id}/email`)).data);
export const useOpenRun = () => useAction(async (month_year: string) => (await api.post<PayrollRun>(`${BASE}/runs`, { month_year })).data);
export const useSubmitRun = () => useAction(async (id: string) => (await api.post<{ message: string }>(`${BASE}/runs/${id}/submit`)).data);
export const useApproveRun = () => useAction(async (id: string) => (await api.post<{ message: string }>(`${BASE}/runs/${id}/approve`)).data);
export const useRejectRun = () => useAction(async ({ id, reason }: { id: string; reason: string }) => (await api.post<{ message: string }>(`${BASE}/runs/${id}/reject`, { reason })).data);

export interface CompensationInput { user_id: string; base_salary: number; structure_code?: string | null; bonus?: number; incentive_percent?: number | null; effective_from?: string; change_reason?: string }
export const useSaveCompensation = () => useAction(async (input: CompensationInput) => (await api.post(`${BASE}/compensation`, input)).data);

export const useSaveMonthlyInput = () => useAction(async (input: { user_id: string; month_year: string; earnings: PayslipLine[]; deductions: PayslipLine[]; notes?: string }) => (await api.put(`${BASE}/monthly-input`, input)).data);
export const useDeleteMonthlyInput = () => useAction(async (id: string) => (await api.delete(`${BASE}/monthly-input/${id}`)).data);

export type ComponentInput = Omit<SalaryComponent, 'id'>;
export const useSaveComponent = () => useAction(async ({ code, isNew, body }: { code: string; isNew: boolean; body: Partial<ComponentInput> }) =>
  (isNew ? await api.post(`${BASE}/components`, body) : await api.put(`${BASE}/components/${code}`, body)).data);
export const useSaveStructure = () => useAction(async ({ code, isNew, body }: { code: string; isNew: boolean; body: Partial<Omit<SalaryStructure, 'id'>> }) =>
  (isNew ? await api.post(`${BASE}/structures`, body) : await api.put(`${BASE}/structures/${code}`, body)).data);
export const useSaveSettings = () => useAction(async (body: Partial<PayrollSettings>) => (await api.put<PayrollSettings>(`${BASE}/settings`, body)).data);

export const useSaveFieldMapping = () => useAction(async (mappings: FieldMappingEntry[]) => (await api.post(`${BASE}/field-mapping`, { mappings })).data);
export const usePreviewExcel = () => useMutation({
  mutationFn: async ({ file, mappings }: { file: File; mappings?: FieldMappingEntry[] }) => {
    const form = new FormData();
    if (mappings) form.append('mappings', JSON.stringify(mappings));
    form.append('file', file);
    return (await api.post<ExcelPreviewResult>(`${BASE}/upload-excel/preview`, form, { headers: { 'Content-Type': null } })).data;
  },
});
export const useUploadPayrollExcel = () => useAction(async ({ file, month_year, mappings, overwrite }: { file: File; month_year: string; mappings?: FieldMappingEntry[]; overwrite?: boolean }) => {
  const form = new FormData();
  form.append('month_year', month_year);
  form.append('overwrite', overwrite ? 'true' : 'false');
  if (mappings) form.append('mappings', JSON.stringify(mappings));
  form.append('file', file);
  return (await api.post<{ message: string; created: number; skipped: number; errors: string[] }>(`${BASE}/upload-excel`, form, { headers: { 'Content-Type': null } })).data;
});

/** Payslips need the login token, so letters and files are fetched and shown from a blob. */
export async function fetchPayslipHtml(id: string): Promise<string> {
  const { data } = await api.get<string>(`${BASE}/${id}/document`, { responseType: 'text', transformResponse: (r) => r });
  return data;
}

function saveBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function downloadPayslipPdf(id: string, name: string) {
  const { data } = await api.get<Blob>(`${BASE}/${id}/pdf`, { responseType: 'blob' });
  saveBlob(data, `${name.replace(/[^a-z0-9-_ ]/gi, '').trim() || 'Payslip'}.pdf`);
}

export async function downloadMonthZip(month: string) {
  const { data } = await api.get<Blob>(`${BASE}/bulk-download`, { params: { month_year: month }, responseType: 'blob' });
  saveBlob(data, `payslips-${month}.zip`);
}
